import React from 'react';
import { Box, Stack } from '@mui/material';
import { Skeleton } from '@repo/ui';
import { LIST_ROW_LEADING_SIZE } from 'components/UI';

/** Matches the 18px glyph `ListRowIcon` renders into the same slot. */
const ICON_PLACEHOLDER_SIZE = 18;

interface ActivityListSkeletonProps {
  /** Number of placeholder rows. Defaults to a screen's worth on mobile. */
  rows?: number;
}

/**
 * Loading placeholder for the Activity timeline (VERA-978).
 *
 * Mirrors the real row's footprint — the leading slot plus the two stacked text
 * lines (label / actor) and the right-aligned timestamp, at the same py:2 row
 * padding — so the swap to loaded content causes no layout shift. Replaces the
 * hand-rolled grey boxes the tab shipped with, per the skeleton standing rule
 * (§19): one `Skeleton` primitive from `@repo/ui`, never a bespoke placeholder.
 *
 * The leading placeholder is a GLYPH-sized square held inside a full
 * `LIST_ROW_LEADING_SIZE` box, matching `ListRowIcon`: the slot keeps the text
 * column on its rail (so nothing shifts sideways on load) while the placeholder
 * itself claims only the space the 18px icon actually fills. It was a 40px circle
 * back when the row drew a disc. `ScansListSkeleton` still uses the 40px circle —
 * correctly, because the Scans row still renders a 40px `Avatar`.
 */
export const ActivityListSkeleton: React.FC<ActivityListSkeletonProps> = ({ rows = 6 }) => {
  return (
    <Stack spacing={0} aria-busy='true' aria-label='Loading activity'>
      {Array.from({ length: rows }).map((_, i) => (
        <Box
          key={i}
          sx={theme => ({
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2,
            borderBottom: `1px solid ${theme.palette.grey[200]}`,
          })}
        >
          <Box
            sx={{
              width: LIST_ROW_LEADING_SIZE,
              height: LIST_ROW_LEADING_SIZE,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Skeleton variant='rounded' width={ICON_PLACEHOLDER_SIZE} height={ICON_PLACEHOLDER_SIZE} />
          </Box>
          <Stack spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
            <Skeleton width='45%' height={14} />
            <Skeleton width='25%' height={12} />
          </Stack>
          <Skeleton width={70} height={12} />
        </Box>
      ))}
    </Stack>
  );
};

export default ActivityListSkeleton;
